import { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { SEOHead } from './SEOHead';
import 'react-circular-progressbar/dist/styles.css';
import { Helmet } from 'react-helmet';

// Lazy load non-critical components
const ScoreDisclaimer = lazy(() => import('./ScoreDisclaimer').then(module => ({ default: module.ScoreDisclaimer })));
const APExamFAQ = lazy(() => import('./APExamFAQ').then(module => ({ default: module.APExamFAQ })));
const DetailedFreeResponse = lazy(() => import('./DetailedFreeResponse').then(module => ({ default: module.DetailedFreeResponse })));
const Footer = lazy(() => import('../Footer'));

// Loading placeholder
const LoadingPlaceholder = () => (
  <div className="p-4 bg-gray-100 animate-pulse rounded-lg h-24"></div>
);

export function APCalculatorBase({ config }) {
  const [mcqScore, setMcqScore] = useState(0);
  const [frqScores, setFrqScores] = useState(
    config.frqQuestions.map(() => 0)
  );

  const calculateAPScore = () => {
    const mcqPercentage = (mcqScore / config.mcqTotal) * config.mcqWeight;
    
    const frqPercentage = frqScores.reduce((sum, score, index) => {
      const question = config.frqQuestions[index];
      return sum + (score / question.maxScore) * question.weight;
    }, 0);

    const totalPercentage = (mcqPercentage + frqPercentage) * 100;

    if (totalPercentage >= 85) return 5;
    if (totalPercentage >= 70) return 4;
    if (totalPercentage >= 50) return 3;
    if (totalPercentage >= 35) return 2;
    return 1;
  };

  const updateFRQScore = (index, value) => {
    const newScores = [...frqScores];
    newScores[index] = value;
    setFrqScores(newScores);
  };

  const score = calculateAPScore();
  const percentage = ((score - 1) / 4) * 100;
  
  // Get a text description of the AP score
  const getScoreDescription = (apScore) => {
    switch(apScore) {
      case 5: return "Extremely well qualified";
      case 4: return "Well qualified";
      case 3: return "Qualified";
      case 2: return "Possibly qualified";
      default: return "No recommendation";
    }
  };
  
  // Extract exam name from title for use in content
  const examName = config.title.split(" Score")[0];
  const examYear = config.title.includes("2025") ? "2025" : "2024";

  // Simplified animation settings for better performance
  const fadeInAnimation = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.3 }
  };

  return (
    <>
      <SEOHead
        title={config.title}
        description={config.description}
        keywords={config.keywords}
        path={config.path}
      />
      
      {/* Add structured data for rich results */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": `${examName} Score Calculator`,
            "description": `Free tool to calculate and estimate your ${examName} score based on College Board scoring guidelines`,
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
        {/* Preload critical assets */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Critical content first - title and calculator */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{config.title}</h1>
          <p className="text-gray-600">{config.description}</p>
          <p className="text-gray-500 mt-2">Updated for {examYear} exam format and scoring guidelines</p>
        </div>

        <div className="bg-blue-50 p-5 rounded-lg mb-8 border border-blue-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-2">About This {examName} Calculator</h2>
          <p>This free calculator uses College Board's official scoring guidelines to estimate your AP score. Enter your multiple choice and free response scores to get an instant estimate of your {examName} exam score on the 1-5 scale.</p>
        </div>

        <div className="grid gap-6">
          {/* MCQ Section - Critical for user interaction */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Multiple Choice Section</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Correct Answers (0-{config.mcqTotal})
                </label>
                <div className="flex gap-4">
                  <input
                    type="range"
                    min="0"
                    max={config.mcqTotal}
                    value={mcqScore}
                    onChange={(e) => setMcqScore(parseInt(e.target.value))}
                    className="flex-1"
                    aria-label="Multiple choice score slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max={config.mcqTotal}
                    value={mcqScore}
                    onChange={(e) => setMcqScore(Math.min(config.mcqTotal, Math.max(0, parseInt(e.target.value) || 0)))}
                    className="w-20 px-3 py-1 border rounded-md text-center"
                    aria-label="Multiple choice score input"
                  />
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Weight: {(config.mcqWeight * 100).toFixed(1)}% of total score
                </div>
              </div>
            </div>
          </div>

          {/* FRQ Section - Lazy loaded but still important */}
          <Suspense fallback={<LoadingPlaceholder />}>
            <DetailedFreeResponse
              questions={config.frqQuestions}
              scores={frqScores}
              onScoreChange={updateFRQScore}
            />
          </Suspense>
        </div>

        {/* Score Display - Critical content */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 p-8 rounded-lg text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Estimated AP Score</h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="w-32 h-32">
              <CircularProgressbar
                value={percentage}
                text={score.toString()}
                styles={buildStyles({
                  textSize: '32px',
                  pathColor: `rgba(255, 255, 255, ${percentage / 100})`,
                  textColor: '#ffffff',
                  trailColor: 'rgba(255, 255, 255, 0.2)',
                })}
              />
            </div>
            <div className="text-left">
              <p className="text-xl mb-2">Score Rating: <span className="font-bold">{getScoreDescription(score)}</span></p>
              <p className="text-lg">College Credit: <span className="font-medium">{score >= 3 ? "Likely eligible" : "May not be eligible"}</span></p>
            </div>
          </div>
          <p className="mt-4 text-blue-100">
            Based on College Board scoring guidelines for {examYear}
          </p>
        </div>

        {/* Non-critical content below - lazy loaded */}
        <Suspense fallback={<LoadingPlaceholder />}>
          <section className="mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use This {examName} Score Calculator</h2>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Enter the number of multiple choice questions you answered correctly</li>
              <li>Input your estimated scores for each free response question</li>
              <li>The calculator automatically applies College Board's scoring formula</li>
              <li>View your estimated AP score on the 1-5 scale</li>
            </ol>
          </section>

          <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Score Guide</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[5, 4, 3, 2, 1].map((level) => (
                <div key={level} className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{level}</div>
                  <div className="text-sm text-gray-600">
                    {level === 5 ? 'Extremely well qualified' :
                     level === 4 ? 'Well qualified' :
                     level === 3 ? 'Qualified' :
                     level === 2 ? 'Possibly qualified' :
                     'No recommendation'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Suspense>

        {/* Remaining content - lazy loaded with low priority */}
        <Suspense fallback={<LoadingPlaceholder />}>
          <section className="my-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{examName} Score Distribution</h2>
            <p className="mb-4">Based on recent exam administrations, here's how students typically perform on the {examName} exam:</p>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-2 px-4 border">AP Score</th>
                    <th className="py-2 px-4 border">Percentage of Students</th>
                    <th className="py-2 px-4 border">College Course Equivalent</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-2 px-4 border font-semibold">5</td>
                    <td className="py-2 px-4 border">~15-20%</td>
                    <td className="py-2 px-4 border">A / A+</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border font-semibold">4</td>
                    <td className="py-2 px-4 border">~20-25%</td>
                    <td className="py-2 px-4 border">A- / B+</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border font-semibold">3</td>
                    <td className="py-2 px-4 border">~25-30%</td>
                    <td className="py-2 px-4 border">B / B-</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border font-semibold">2</td>
                    <td className="py-2 px-4 border">~15-20%</td>
                    <td className="py-2 px-4 border">C / C+</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 border font-semibold">1</td>
                    <td className="py-2 px-4 border">~10-15%</td>
                    <td className="py-2 px-4 border">D / F</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-sm text-gray-500">Note: Actual distributions vary by year and subject. These are approximate ranges.</p>
          </section>

          <APExamFAQ examName={examName} />
          
          <section className="my-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Tips for Success on the {examName} Exam</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">Multiple Choice Strategy</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Read each question carefully before selecting an answer</li>
                  <li>Eliminate obviously incorrect answers first</li>
                  <li>Pace yourself to complete all questions</li>
                  <li>If unsure, make an educated guess (no penalty for wrong answers)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Free Response Strategy</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Answer all parts of each question</li>
                  <li>Show all your work clearly</li>
                  <li>Use appropriate terminology and notation</li>
                  <li>Practice time management to complete all questions</li>
                </ul>
              </div>
            </div>
          </section>

          <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
            <h2 className="text-xl font-semibold text-blue-800 mb-2">Need Help Improving Your {examName} Score?</h2>
            <p className="mb-4">Use our free {examName} score calculator to track your progress as you prepare for the exam. For personalized study plans and expert guidance, explore our AP prep resources.</p>
            <p>Remember that this calculator provides an estimate based on official scoring guidelines. Your actual AP score may vary slightly.</p>
          </div>

          <ScoreDisclaimer />
          <Footer />
        </Suspense>
      </div>
    </>
  );
}

